import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ITableColumn } from "../../../../components";
import DataTable from "../../../../components/components/Table/DataTable";
import { policyAutoVehiclesReadDto } from "../../../model/PolicyVehicleDto";
import CustomerBasicVehicleForm from "./CustomerBasicVehicleForm";
import CustomerBasicVehicleService from "./CustomerBasicVehicleService";
import { RootState } from "../../../../shared/store";
import { updateSelectedPolicyDetails  } from "../../../customer-slice/Customer-slice";
import { uuid } from "../../../../shared/util/uuid";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { useConfirmationModalContext } from "../../../../shared/components/ModalConfirmationContext";
import { CustomerPolicyDetails } from "../../../../shared/util/constant";

export interface ICustomerBasicVehicleGrid {
    customerId?: string;
    policyId?: string;
    polEventId?: string;
    validationErrors?: any;
    tabIndex?: number;
}

const CustomerBasicVehicleGrid: React.FunctionComponent<ICustomerBasicVehicleGrid> = (props) => {
    let baseTabIndex = props.tabIndex;
    const { customerId, policyId, polEventId, validationErrors } = props;
    const dispatch = useDispatch();
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [isFormSaved, setIsFormSaved] = useState(true);
    const [currentVehicleData, setCurrentVehicleData] = useState<policyAutoVehiclesReadDto | null>(null);
    const customerDetail = useSelector((state: RootState) => state?.customerSlice);
    const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, polEventId);
    const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
    const [policyAutoVehicles, setPolicyVehicles] = useState<policyAutoVehiclesReadDto[]>([]);
    const vehicleService = new CustomerBasicVehicleService();
    const modalContext = useConfirmationModalContext();

    useEffect(() => {
        if (policyDetails && policyDetails.policyLineOfBusinesses) {
            const lob = policyDetails.policyLineOfBusinesses.find((l) => l.polEventId === polEventId);
            if (lob && lob.policyAutoVehicles) {
                const sorted = [...lob.policyAutoVehicles].sort((a, b) => {
                    if (a.vehicleNumber != null && b.vehicleNumber != null) {
                        return Number(a.vehicleNumber) - Number(b.vehicleNumber);
                    }
                    if (a.vehicleNumber == null) return 1;
                    if (b.vehicleNumber == null) return -1;
                    return 0;
                });
                setPolicyVehicles(sorted);
                setCurrentVehicleData(sorted[0] || null);
            }
        }
    }, [policyId, polEventId]);

    const onRowClick = (rowData: any) => {
        if (expandedRowId === rowData.id) {
            setExpandedRowId(null);
            setCurrentVehicleData(null);
        } else {
            setExpandedRowId(rowData.id);
            setCurrentVehicleData(rowData);
            onVehicleSelect(rowData);
        }
    };

    const onAddNewVehicleClick = () => {
        const lob = policyDetails?.policyLineOfBusinesses.find((l) => l.polEventId === polEventId);
        if (!lob) return;

        let nextVehicleNumber = 1;
        if (policyAutoVehicles && policyAutoVehicles.length > 0) {
            const maxLocationNumber = Math.max(
                ...policyAutoVehicles.map(v => v.vehicleNumber || 0)
            );
            nextVehicleNumber = maxLocationNumber + 1;
        }
        // if (policyAutoVehicles  && policyAutoVehicles .length > 0) {
        //     const maxNum = Math.max(...policyAutoVehicles.map((v) => v.vehicleNumber || 0));
        //     nextVehicleNumber = maxNum + 1;
        // }

        const newVehicle: policyAutoVehiclesReadDto = {
            id: uuid(),
            polId: policyId,
            polEventId: polEventId,
            lobId: lob.id,
            vehicleNumber: nextVehicleNumber,
            effDate: policyDetails?.polEffDate ? CommonUtility.datetimeToDateOnly(policyDetails.polEffDate) : "",
            VIN: "",
            RegisterState: "",
            VehicleYear: "",
            Make: "",
            Model: "",
            BodyType: "",
            IsVehicleNew: "",
            PurchaseDate: "",
            isLeased: "",
            leasedDate: "",
            costNew: "",
        };

        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
        if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
            updatedPolicyDetails.policyLineOfBusinesses = updatedPolicyDetails.policyLineOfBusinesses.map((item: any) => {
                if (item.polEventId === polEventId) {
                    const vehicles = item.policyAutoVehicles ?? [];
                    return {
                        ...item,
                        policyAutoVehicles: [newVehicle, ...vehicles],
                    };
                }
                return item;
            });

            dispatch(
                updateSelectedPolicyDetails({
                    policyId: policyId ?? "",
                    polEventId: polEventId ?? "",
                    policyDetails: updatedPolicyDetails,
                })
            );
        }

        setPolicyVehicles([newVehicle, ...policyAutoVehicles]);
        onVehicleSelect(newVehicle);
        setExpandedRowId(newVehicle.id);
        setIsFormSaved(false);
    };

    const onRowDeleteClick = async (rowData: any, column: ITableColumn) => {
        try {
            const result = await modalContext.showConfirmation(CustomerPolicyDetails.DELETE_VEHICLE);
            if (!result) return;

            const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
            if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
                const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map((lob: any) => {
                    if (lob.polEventId === polEventId) {
                        return {
                            ...lob,
                            policyAutoVehicles: (lob.policyAutoVehicles ?? []).filter((v: policyAutoVehiclesReadDto) => v.id !== rowData.id),
                        };
                    }
                    return lob;
                });

                updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;

                const filtered = policyAutoVehicles.filter((v) => v.id !== rowData.id);
                setPolicyVehicles(filtered);

                if (currentVehicleData?.id === rowData.id) {
                    setCurrentVehicleData(filtered.length > 0 ? filtered[0] : null);
                }

                dispatch(
                    updateSelectedPolicyDetails({
                        policyId: policyId,
                        polEventId: polEventId,
                        policyDetails: updatedPolicyDetails,
                    })
                );
                setExpandedRowId(null);
                setIsFormSaved(true);
            }
        } catch (ex) {
            console.error("Error deleting vehicle", ex);
            setIsFormSaved(true);
        }
    };

    const onVehicleSelect = (rowData: policyAutoVehiclesReadDto) => {
        setCurrentVehicleData(rowData);
    };

    const onCancelClick = () => {
        // Reset other states
        setExpandedRowId(null);
        setIsFormSaved(true);
    };

    const tableColumns: ITableColumn[] = vehicleService.getVehicleTableColumns(
        onRowClick,
        onVehicleSelect,
        onCancelClick,
        onRowDeleteClick,
        currentVehicleData?.id,
        baseTabIndex
    );

    const renderRow = (vehicle: policyAutoVehiclesReadDto, index: number) => {
        return (
            <React.Fragment key={vehicle.id || index}>
                {expandedRowId === vehicle.id && (
                    <tr>
                        <td colSpan={tableColumns.length}>
                            <CustomerBasicVehicleForm
                                customerId={customerId}
                                polId={policyId}
                                polEventId={polEventId}
                                vehicleId={vehicle.id}
                                tabIndex={baseTabIndex}
                                policyVehicle={currentVehicleData!}
                                setIsFormSaved={setIsFormSaved}
                                formValidation={validationErrors}
                            />
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    return (
        <div className="customer-policy-location-grid">
            <Button className="mb-2" tabIndex={baseTabIndex} onClick={onAddNewVehicleClick}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Vehicle
            </Button>

            <DataTable
                columns={tableColumns}
                data={policyAutoVehicles}
                keyColumnName="id"
                expandedRowId={expandedRowId}
                setExpandedRowId={setExpandedRowId}
                renderRow={renderRow}
                isFormSaved={isFormSaved}
                tableClassName="table-striped table-hover table-sm"
                theadClassName="tblHeader"
            />
        </div>
    );
};

export default CustomerBasicVehicleGrid;
